import {emotionStyled, tokens} from '@packages/utilities/styled';
import {RowItemContainer, RowItemActiveSelectors} from '../RowItem.styles';

import {Text} from '../../Text';
import {Link} from '../../Link';
import {Icon} from '../../../icons/Icon';
import {RowItemInputLabelWrapper} from '../RowItemInput/RowItemInput.styles';

const MULTILINE_PADDING = '4px';
export const SELECTED_BACKGROUND_COLOR =
  '--local-row-item-selected-background-color';
const BACKGROUND_COLOR_VAR = `var(${SELECTED_BACKGROUND_COLOR}, $uiBackgroundHover)`;

export const ROW_ITEM_MARGIN_VAR = '--row-item-margin';

export const RowItemView = emotionStyled(Link)(
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: tokens.ui.rowItem.spacing,
    color: '$textPrimary',
    cursor: 'default',
    contain: 'layout',
    '&:is([role="option"], [role="button"], a)': {
      cursor: tokens.ui.activeCursor, // All interactive content in Designer uses `cursor: default`
    },
    /**
     * aria-checked is used for the Checkbox component.
     * aria-selected is used for the SearchableList component.
     */
    '&[aria-selected="true"]': {
      backgroundColor: BACKGROUND_COLOR_VAR,
    },
    [`${RowItemContainer}:hover &:not([aria-disabled="true"])`]: {
      textDecoration: 'none',
    },
    /**
     * We only want to show the hover state when the item is not disabled or controlled by the internal _isActive prop.
     *
     * Regardless of colorVariant, if an item is not selected, we use uiBackgroundHover.
     */
    [`${RowItemContainer}:hover:not(:has(${RowItemInputLabelWrapper})) &:not([aria-disabled="true"], [data-controlled-by-active="true"])`]:
      {
        backgroundColor: '$uiBackgroundHover',
        /**
         * If the item is selected and has a colorVariant, we want to use the colorVariant background color.
         *
         * aria-checked is used for the Checkbox component.
         * aria-selected is used for the SearchableList component.
         */
        '&[aria-selected="true"], &[aria-checked="true"]': {
          backgroundColor: BACKGROUND_COLOR_VAR,
        },
      },
    // We need to override the Spring default styling for Links.
    '&:focus-visible': {
      borderRadius: '$2',
      outline: '$blueBorder',
      outlineOffset: '-1px',
      textDecoration: 'none !important',
    },
    [`& ${Icon}:first-of-type`]: {
      marginRight: '0 !important',
    },
    [`& ${Icon}:last-of-type`]: {
      marginLeft: '0 !important',
    },
  },
  {
    componentName: 'RowItem',
    variants: {
      colorVariant: {
        default: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.uiBackgroundHover,
        },
        blue: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.blueTransparent,
        },
        green: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.greenTransparent,
        },
        orange: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.orangeTransparent,
        },
        purple: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.purpleTransparent,
        },
        pink: {
          [SELECTED_BACKGROUND_COLOR]: tokens.colors.pinkTransparent,
        },
      },
      density: {
        compact: {
          paddingTop: tokens.ui.rowItem.padding.compact.vertical,
          paddingBottom: tokens.ui.rowItem.padding.compact.vertical,
          paddingLeft: tokens.ui.rowItem.padding.compact.left,
          paddingRight: tokens.ui.rowItem.padding.compact.right,
        },
        comfortable: {
          paddingTop: tokens.ui.rowItem.padding.comfortable.vertical,
          paddingBottom: tokens.ui.rowItem.padding.comfortable.vertical,
          paddingLeft: tokens.ui.rowItem.padding.comfortable.left,
          paddingRight: tokens.ui.rowItem.padding.comfortable.right,
        },
      },
      isActive: {
        true: {
          /**
           * We are increasing specificity in these selectors to override the '&:not()' condition above for the _isActive prop.
           */
          '&&&:not([data-has-checkmark="true"])': {
            backgroundColor: `${BACKGROUND_COLOR_VAR}`,
          },
          '&[aria-disabled="true"]': {
            '&&&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      isDraggedItem: {
        true: {
          /**
           * We want to target _everything_ here, since the mouse moves faster than the dragged overlay.
           * If we only targetted this div, the cursor would flicker between the default and grabbing cursor.
           */
          'body:has(&) *': {
            cursor: 'grabbing !important',
          },
          /**
           * Increase specificity to override the default hover state.
           */
          [`${RowItemContainer}:hover &&&`]: {
            backgroundColor: '$uiDragOverlayBackground',
          },
          backgroundColor: '$uiDragOverlayBackground',
          boxShadow: '$menu',
        },
      },
      isMultiline: {
        true: {},
      },
      isRounded: {
        true: {
          borderRadius: '$2',
        },
      },
      hasCheckmark: {
        true: {
          '&[aria-selected="true"]': {
            background: 'none',
          },
        },
      },
      shouldHighlightOnChecked: {
        true: {
          '&[aria-checked="true"]': {
            backgroundColor: BACKGROUND_COLOR_VAR,
          },
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isMultiline: true,
          density: 'comfortable',
        },
        css: {
          paddingTop: `calc(${tokens.ui.rowItem.padding.comfortable.vertical} + ${MULTILINE_PADDING})`,
          paddingBottom: `calc(${tokens.ui.rowItem.padding.comfortable.vertical} + ${MULTILINE_PADDING})`,
        },
      },
      {
        conditions: {
          isMultiline: true,
          density: 'compact',
        },
        css: {
          paddingTop: `calc(${tokens.ui.rowItem.padding.compact.vertical} + ${MULTILINE_PADDING})`,
          paddingBottom: `calc(${tokens.ui.rowItem.padding.compact.vertical} + ${MULTILINE_PADDING})`,
        },
      },
    ],
    defaultVariants: {
      density: 'comfortable',
    },
  }
);

/**
 * We are mimicking the padding for an Icon button to preserve the height of the RowItemBody.
 * The density of a RowItem is supplied by its padding (passed into RowItemView).
 *
 * We are using `16px` here because it's the default size for our Icon components.
 * Our `RowItemAction` and `RowItemChevron` do not override this value.
 */
export const ROW_ITEM_BODY_MIN_HEIGHT = `calc(calc(${tokens.ui.iconButton.padding.comfortable} * 2) + 16px)`;

export const RowItemBodyView = emotionStyled('div')(
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: tokens.ui.rowItem.content.spacing,
    minWidth: '0px', // prevent overflow in flex parent
    userSelect: 'none',
    minHeight: ROW_ITEM_BODY_MIN_HEIGHT,
    marginRight: `var(${ROW_ITEM_MARGIN_VAR}, 0px)`,
  },
  {
    componentName: 'RowItemBody',
    variants: {
      isTopAligned: {
        true: {
          [`& ${Icon}[data-row-prefix]`]: {
            alignSelf: 'start',
            marginTop: tokens.ui.rowItem.prefix.marginTop, // Account for different sized checkbox squares and align with Icons.
          },
        },
      },
      iconColorVariant: {
        blue: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$blueIcon',
          },
        },
        green: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$greenIcon',
          },
        },
        orange: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$orangeIcon',
          },
        },
        pink: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$pinkIcon',
          },
        },
        purple: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$purpleIcon',
          },
        },
        default: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$textSecondary',
          },
        },
        primary: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$textPrimary',
          },
        },
        secondary: {
          [`& ${Icon}[data-row-prefix]`]: {
            color: '$textSecondary',
          },
          [RowItemActiveSelectors()]: {
            [`& ${Icon}[data-row-prefix]`]: {
              color: '$textPrimary',
            },
          },
        },
      },
    },
  }
);

export const RowItemLabelView = emotionStyled('div')(
  {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '$1',
    width: '100%',
    minWidth: '0px', // prevent overflow in flex parent
    position: 'relative',
    minHeight: tokens.typography.lineHeight.body,
  },
  {
    componentName: 'RowItemLabel',
    variants: {
      isMultiline: {
        true: {
          alignItems: 'start',
          flexDirection: 'column',
          gap: '$0',
        },
      },
    },
  }
);

export const RowItemTitle = emotionStyled(Text)(
  {
    flex: 1.5,
    maxWidth: 'fit-content',
  },
  {
    componentName: 'RowItemTitle',
    variants: {
      isRenaming: {
        true: {
          opacity: 0,
          visibility: 'hidden',
        },
      },
    },
  }
);

export const RowItemDescription = emotionStyled(Text)(
  {
    flex: 1,
    maxWidth: 'fit-content',
    position: 'relative',
    '@media (prefers-reduced-motion: no-preference)': {
      transition: 'transform 0.15s ease-in-out',
    },
  },
  {
    componentName: 'RowItemDescription',
    variants: {
      isMultiline: {
        true: {},
      },
      isRenaming: {
        true: {
          opacity: 0,
          visibility: 'hidden',
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isMultiline: true,
          isRenaming: true,
        },
        css: {
          opacity: 1,
          visibility: 'visible',
          transform: 'translateY(4px)',
        },
      },
    ],
  }
);

/**
 * This is a minimal hit area for the title to be double-clickable.
 * This solves for a problem where the title is too small (i.e. below 20x20px) to be accessible.
 *
 * Because the Title has overflow: hidden, this needs to live outside of the Title's bounds.
 */
export const RowItemTitleHitArea = emotionStyled('div')(
  {
    position: 'absolute',
    top: -2,
    left: -2,
    minWidth: '20px',
    minHeight: '20px',
  },
  {
    componentName: 'RowItemTitleHitArea',
  }
);

export const RowItemCheckboxWrapper = emotionStyled('span')(
  {},
  {
    componentName: 'RowItemCheckboxWrapper',
    variants: {
      isTopAligned: {
        true: {
          alignSelf: 'start',
          marginTop: tokens.ui.rowItem.checkbox.marginTop, // Account for different sized checkbox squares and align with Icons.
        },
      },
    },
  }
);
